import { useEffect, useState } from "react";
import { useLinks } from "../state/auth";
import { hasScore } from "../pdf/pdfStore";
import { openSong } from "../core/songUrl";
import { BookmarkIcon } from "./icons";
import { useT, useLang } from "../i18n";

const LOCALE = { ko: "ko-KR", en: "en-US", ja: "ja-JP" } as const;

// "내 링크": the songs saved to the account. A PDF sheet is never on the account — the badge only
// says this device has one for the song (it opens with the song, from this browser's storage).
export function MyLinks({ onClose }: { onClose: () => void }) {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const links = useLinks((s) => s.links);
  const loaded = useLinks((s) => s.loaded);
  const remove = useLinks((s) => s.remove);
  const [withPdf, setWithPdf] = useState<Set<string>>(new Set());
  const fmtDate = (sec: number) =>
    new Date(sec * 1000).toLocaleDateString(LOCALE[lang], { year: "numeric", month: "short", day: "numeric" });

  useEffect(() => { useLinks.getState().refresh(); }, []);
  useEffect(() => {
    let live = true;
    Promise.all(links.map(async (l) => ((await hasScore("yt:" + l.vid).catch(() => false)) ? l.vid : null)))
      .then((v) => { if (live) setWithPdf(new Set(v.filter((x): x is string => !!x))); });
    return () => { live = false; };
  }, [links]);

  const open = (vid: string) => { openSong(vid); onClose(); };
  const del = (vid: string) => { if (confirm(t("links.delete_confirm"))) remove(vid); };

  return (
    <div className="links-page">
      <header className="links-topbar">
        <button className="links-back" onClick={onClose} aria-label={t("links.close")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <div className="links-ttl">{t("links.title")}</div>
        <div className="links-count mono">{loaded ? t("links.count", { n: links.length }) : ""}</div>
      </header>

      <div className="links-body">
        {!loaded ? null : links.length === 0 ? (
          <div className="lk-empty">
            <div className="lk-empty-ic"><BookmarkIcon size={28} /></div>
            <div>
              <div className="lk-empty-h">{t("links.empty")}</div>
              <div className="lk-empty-p">{t("links.empty_sub")}</div>
            </div>
          </div>
        ) : (
          <ul className="links-list">
            {links.map((l) => (
              <li key={l.vid} className="lk-card" onClick={() => open(l.vid)} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(l.vid); } }}>
                <div className="lk-thumb">
                  <img src={`https://i.ytimg.com/vi/${l.vid}/mqdefault.jpg`} alt="" loading="lazy" />
                  <span className="lk-play"><svg width="9" height="9" viewBox="0 0 9 9"><path d="M1.5 1L8 4.5 1.5 8z" fill="#fff" /></svg></span>
                </div>
                <div className="lk-mid">
                  <div className="lk-title">{l.title}</div>
                  <div className="lk-meta">
                    {l.key && <span className="lk-badge">{l.key}</span>}
                    {l.bpm ? <span className="lk-badge"><span className="mono">♩={l.bpm}</span></span> : null}
                    {withPdf.has(l.vid) && <span className="lk-badge pdf" title={t("links.pdf_here_hint")}>{t("links.pdf_here")}</span>}
                    <span className="lk-date">{fmtDate(l.added_at)}</span>
                  </div>
                </div>
                <div className="lk-act">
                  <span className="lk-go" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                  </span>
                  <button className="lk-del" title={t("links.delete")} aria-label={t("links.delete")}
                    onClick={(e) => { e.stopPropagation(); del(l.vid); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
